import { ReactNode } from 'react';
import { Header } from './Header';
import { Sidebar } from './Sidebar';
import { Footer } from './Footer';
import { ErrorBoundary } from '../ErrorBoundary';
import { Tutorial } from '../../components/Tutorial';
import { useTutorialStore } from '../../stores/tutorialStore';

interface MainLayoutProps {
  children: ReactNode;
  sidebarOpen: boolean;
  setSidebarOpen: (open: boolean) => void;
}

export function MainLayout({ children, sidebarOpen, setSidebarOpen }: MainLayoutProps) {
  const isActive = useTutorialStore((state) => state.isActive);

  return (
    <div 
      className="min-h-screen bg-gradient-to-br from-obsidian-900 to-obsidian-800"
      data-tutorial-active={isActive}
    >
      {/* Fixed Header */}
      <div className="fixed top-0 left-0 right-0 z-[1000]">
        <Header sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />
      </div>
      
      {/* Main Content Area with padding for fixed header */}
      <div className="pt-20 lg:pt-16">
        <div className="relative flex">
          {/* Mobile/Tablet Overlay */}
          {sidebarOpen && (
            <div 
              className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[90] lg:hidden"
              onClick={() => setSidebarOpen(false)}
            />
          )}

          {/* Sidebar - Fixed position */}
          <div 
            className={`fixed lg:fixed top-20 lg:top-16 h-[calc(100vh-5rem)] lg:h-[calc(100vh-4rem)] transition-transform duration-300 z-[95]
              ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
              ${sidebarOpen ? 'w-[280px] sm:w-[320px] lg:w-64' : 'w-64'}`}
          >
            <Sidebar open={sidebarOpen} setOpen={setSidebarOpen} />
          </div>

          {/* Main Content Area with Footer */}
          <div 
            className={`flex-1 transition-all duration-300 w-full
              ${sidebarOpen ? 'lg:ml-64' : 'lg:ml-64'}
              ${sidebarOpen ? 'sm:ml-[320px] lg:ml-64' : 'ml-0 sm:ml-0'}`}
          >
            <main className="p-4 sm:p-6 lg:p-8">
              <div className="max-w-6xl mx-auto">
                <ErrorBoundary>
                  {children}
                </ErrorBoundary>
              </div>
            </main>

            {/* Footer */}
            <Footer />
          </div>
        </div>
      </div>

      {/* Tutorial Component */}
      <Tutorial />
    </div>
  );
}
